// CompoundPicker — replaces the legacy TierGrid / TierDetailPanel pair
// on the program-detail page. Three compound cards across the top
// (Semaglutide / Tirzepatide / Retatrutide) → tap a card → expands an
// inline detail panel below with a stage slider (Start ◀ ▶ Maintain),
// per-stage pricing, and the compound's rich research / safety content.
//
// Design promise: same look-and-feel as the legacy tier picker — peach
// /sage/ink palette, editorial type, diagonal-cut transitions. Only the
// data shape changes (compound + stage instead of tier).
//
// Tray mapping (Phase A — submission flow stays legacy):
//   tierId   = stage.productId
//   tierKey  = `${compound.slug}:${stage.stageKey}`   (synthetic)
//   tierName = `${compound.name} · ${stage.stageLabel}`
//   monthlyPrice = stage.monthlyPrice
// When BE Phase B ships compound-aware submission, we'll add
// selectedCompoundId / selectedStage alongside these fields without
// breaking the existing flow.
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import InvestigationalCallout from "@/components/InvestigationalCallout";
import SelfAwarenessQuestions from "@/components/SelfAwarenessQuestions";
import { C } from "@/lib/brand";
import { fmtMoney } from "@/lib/format";

// stageKey / stageLabel are admin-authored strings. We never hard-code
// "start" / "build" / "maintain" — defaults always come from the
// compound's own stages[] array (first entry).

export default function CompoundPicker({
  compounds,
  decisionHelper,
  programColor,
  programAccent,
  isActiveForPurchase,
  inPlanItem,                // { tierId, tierKey, tierName, ... } | null
  onAddCompound,             // (compound, stage) => void  — only when active
  onRemoveCompound,          // () => void
  programName,
  programSlug,
  // BE Phase D — sub-section grouping. Programs that opt in (currently
  // Recovery & Healing) declare ordered sub-sections like "Tissue
  // Repair Compounds" / "Multi-Mechanism Blends"; each compound carries
  // a `subSectionSlug` pointing at one of them. When this prop is null
  // / empty we fall back to a flat grid (current behavior — preserves
  // Weight Management & Skin & Beauty unchanged).
  subSections,
  beforeDecisionHelper,      // ReactNode — slotted between the compound
                             // cards/detail panel and the DecisionHelper.
                             // Used for the "What to expect" lifestyle
                             // timeline so it sits inside the compound
                             // flow rather than above the picker.
}) {
  const validCompounds = useMemo(
    () =>
      (Array.isArray(compounds) ? compounds : [])
        .filter((c) => c && c.isActive !== false && Array.isArray(c.stages) && c.stages.length > 0)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [compounds],
  );

  const [expandedSlug, setExpandedSlug] = useState(null);
  const detailRef = useRef(null);

  // Reverse-resolve which compound (if any) the member already added to
  // their plan. Spec §5 — selections live in the tray, not in component
  // state — so the in-plan badge matches across all surfaces.
  const inPlanCompoundSlug = useMemo(() => {
    if (!inPlanItem?.tierKey) return null;
    const [slug] = String(inPlanItem.tierKey).split(":");
    return slug || null;
  }, [inPlanItem]);
  const inPlanStageKey = useMemo(() => {
    if (!inPlanItem?.tierKey) return null;
    const parts = String(inPlanItem.tierKey).split(":");
    return parts[1] || null;
  }, [inPlanItem]);

  // Build the ordered sub-section list, dropping any sections that
  // have zero matching compounds. Adds an "uncategorized" trailing
  // bucket so compounds the admin forgot to tag don't disappear.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const subSectionGroups = useMemo(() => {
    const ordered = Array.isArray(subSections) ? [...subSections] : [];
    if (ordered.length === 0) return [];
    ordered.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const groups = ordered.map((s) => ({
      ...s,
      compounds: validCompounds.filter((c) => c.subSectionSlug === s.slug),
    })).filter((g) => g.compounds.length > 0);
    // Trailing "Other" bucket — compounds with no/unknown subSectionSlug
    // STILL appear so members aren't silently shown a partial set.
    const orphaned = validCompounds.filter(
      (c) => !c.subSectionSlug || !ordered.some((s) => s.slug === c.subSectionSlug),
    );
    if (orphaned.length > 0) {
      groups.push({
        slug: "_other",
        displayName: "Other",
        displayOrder: 9999,
        compounds: orphaned,
      });
    }
    return groups;
  }, [subSections, validCompounds]);

  // Single-compound programs (e.g. Sexual Wellness / PT-141) get a
  // streamlined treatment: no "PICK A path." invite, no lonely card
  // in a 3-col grid — we open the detail panel straight away. The
  // Decision Helper's comparative framing is also suppressed in this
  // mode (there's nothing to compare); self-awareness questions still
  // render via the helper below.
  const isSingleCompound = validCompounds.length === 1;

  // Auto-expand the only compound so the detail panel renders
  // immediately on single-compound programs. State sync runs in an
  // effect to avoid infinite re-renders on every prop change.
  useEffect(() => {
    if (isSingleCompound && validCompounds[0] && expandedSlug !== validCompounds[0].slug) {
      setExpandedSlug(validCompounds[0].slug);
    }
  }, [isSingleCompound, validCompounds, expandedSlug]);

  if (validCompounds.length === 0) return null;

  const onLearnMore = (slug) => {
    const next = expandedSlug === slug ? null : slug;
    setExpandedSlug(next);
    if (next != null) {
      requestAnimationFrame(() => {
        detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };

  // Render mode: grouped iff the BE provided meaningful sub-sections
  // AND at least one of them maps to a compound. Otherwise flat.
  const useGroupedLayout = subSectionGroups.length > 0 && subSectionGroups.some((g) => g.slug !== "_other");

  return (
    <div data-testid="compound-picker" data-single-compound={isSingleCompound ? "1" : undefined}>
      <Eyebrow color={programAccent}>
        {isSingleCompound ? "★ THE COMPOUND" : "★ CHOOSE YOUR COMPOUND"}
      </Eyebrow>
      <Headline
        before={isSingleCompound ? "THE" : "PICK A"}
        italic={isSingleCompound ? "compound." : "path."}
        size={48}
        className="mt-4"
      />

      {!isSingleCompound && (useGroupedLayout ? (
        <div data-testid="compound-card-groups" style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 36 }}>
          {subSectionGroups.map((group) => (
            <SubSectionBlock
              key={group.slug}
              group={group}
              programColor={programColor}
              programAccent={programAccent}
              isActiveForPurchase={isActiveForPurchase}
              expandedSlug={expandedSlug}
              inPlanCompoundSlug={inPlanCompoundSlug}
              onLearnMore={onLearnMore}
            />
          ))}
        </div>
      ) : (
        <div
          data-testid="compound-card-grid"
          style={{
            marginTop: 32,
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 14,
          }}
          className="!grid-cols-1 md:!grid-cols-3"
        >
          {validCompounds.map((c) => (
            <CompoundCard
              key={c.slug || c.compoundId}
              compound={c}
              programColor={programColor}
              programAccent={programAccent}
              isActiveForPurchase={isActiveForPurchase}
              isExpanded={expandedSlug === c.slug}
              isInPlan={inPlanCompoundSlug === c.slug}
              onLearnMore={() => onLearnMore(c.slug)}
            />
          ))}
        </div>
      ))}

      {/* Inline detail panel — single instance below the row, with
          compound switcher pills so members can flip between the three
          without scrolling back up to a different card. Mirrors the
          legacy TierDetailPanel pattern. */}
      <CompoundDetailPanel
        ref={detailRef}
        compounds={validCompounds}
        expandedSlug={expandedSlug}
        onSwitch={(slug) => setExpandedSlug(slug)}
        onClose={() => setExpandedSlug(null)}
        programColor={programColor}
        programAccent={programAccent}
        programName={programName}
        isActiveForPurchase={isActiveForPurchase}
        onAddCompound={onAddCompound}
        onRemoveCompound={onRemoveCompound}
        inPlanCompoundSlug={inPlanCompoundSlug}
        inPlanStageKey={inPlanStageKey}
      />

      {/* Slot for cross-section content that should sit between the
          compound picker and the Decision Helper — currently used for
          the "What to expect" lifestyle timeline. */}
      {beforeDecisionHelper}

      {/* Decision Helper — comparison matrix + interactive
          self-awareness questions. The matrix is admin-controlled
          (enabled flag); the questions live behind their own API
          endpoints with per-member persistence. */}
      <DecisionHelper
        helper={decisionHelper}
        compounds={validCompounds}
        programAccent={programAccent}
        programSlug={programSlug}
        isSingleCompound={isSingleCompound}
      />
    </div>
  );
}

/* ---------- sub-section block (grouped layout) ---------- */
// Renders one group: peach eyebrow + optional descriptor + a card grid.
// The grid mirrors the flat layout so the visual rhythm is identical
// inside each group.
function SubSectionBlock({
  group,
  programColor,
  programAccent,
  isActiveForPurchase,
  expandedSlug,
  inPlanCompoundSlug,
  onLearnMore,
}) {
  return (
    <section data-testid={`compound-subsection-${group.slug}`}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: programAccent }}>
        ★ {String(group.displayName || "").toUpperCase()}
      </div>
      {group.description && (
        <p
          style={{
            marginTop: 8,
            fontSize: 14,
            lineHeight: 1.55,
            color: C.warmGray,
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontStyle: "italic",
            maxWidth: 620,
          }}
        >
          {group.description}
        </p>
      )}
      <div
        data-testid={`compound-subsection-grid-${group.slug}`}
        style={{
          marginTop: 18,
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 14,
        }}
        className={group.compounds.length >= 3 ? "!grid-cols-1 md:!grid-cols-3" : "!grid-cols-1 md:!grid-cols-2"}
      >
        {group.compounds.map((c) => (
          <CompoundCard
            key={c.slug || c.compoundId}
            compound={c}
            programColor={programColor}
            programAccent={programAccent}
            isActiveForPurchase={isActiveForPurchase}
            isExpanded={expandedSlug === c.slug}
            isInPlan={inPlanCompoundSlug === c.slug}
            onLearnMore={() => onLearnMore(c.slug)}
          />
        ))}
      </div>
    </section>
  );
}

/* ---------- compound card ---------- */

// WADA-prohibited fallback list. Used when the BE compound payload
// doesn't carry a structured flag. The prompt explicitly authorizes
// hardcoded fallback for BPC-157 + any TB-500-containing blend +
// GHK-Cu + KLOW, flagged for future enhancement (admin-controlled).
const WADA_PROHIBITED_SLUGS = new Set([
  "bpc-157",
  "tb-500",
  "tb500",
  "recovery-blend",
  "ghk-cu",
  "ghkcu",
  "glow",
  "glow-multi-peptide-blend",
  "klow",
  "klow-multi-peptide-blend",
]);

// True when the compound is WADA-prohibited. Prefers BE-supplied
// metadata; falls back to the slug list.
function isWadaProhibited(compound) {
  if (compound?.wadaProhibited === true) return true;
  if (typeof compound?.wadaStatus === "string"
      && /prohibit|banned|wada/i.test(compound.wadaStatus)) return true;
  const slug = String(compound?.slug || "").toLowerCase();
  return WADA_PROHIBITED_SLUGS.has(slug);
}

function CompoundCard({ compound, programColor, programAccent, isActiveForPurchase, isExpanded, isInPlan, onLearnMore }) {
  const startingFrom = useMemo(() => {
    const prices = (compound.stages || [])
      .map((s) => Number(s.monthlyPrice))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (prices.length === 0) return null;
    return Math.min(...prices);
  }, [compound.stages]);

  return (
    <button
      type="button"
      data-testid={`compound-card-${compound.slug}`}
      onClick={onLearnMore}
      style={{
        textAlign: "left",
        background: isExpanded ? C.ink : C.pure,
        color: isExpanded ? C.cream : C.ink,
        border: `1.5px solid ${isExpanded ? C.ink : C.warmGray3}`,
        padding: 22,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        position: "relative",
        transition: "background 160ms ease, color 160ms ease",
      }}
    >
      {isInPlan && (
        <span
          data-testid={`compound-card-in-plan-${compound.slug}`}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: programColor,
            color: programAccent,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.18em",
            padding: "3px 8px",
          }}
        >
          ★ IN PLAN
        </span>
      )}

      {compound.eyebrow && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.22em",
            color: isExpanded ? C.peach : programAccent,
            textTransform: "uppercase",
          }}
        >
          {compound.eyebrow}
        </div>
      )}
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.01em", textTransform: "uppercase", lineHeight: 1.05 }}>
        {compound.name}
      </div>
      {compound.tagline && (
        <p
          style={{
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontStyle: "italic",
            fontSize: 16,
            lineHeight: 1.4,
            color: isExpanded ? "rgba(250,246,240,0.72)" : C.warmGray,
            margin: 0,
          }}
        >
          {compound.tagline}
        </p>
      )}
      {compound.oneLineDescription && (
        <p
          style={{
            fontSize: 13,
            color: isExpanded ? "rgba(250,246,240,0.72)" : C.warmGray,
            lineHeight: 1.55,
            margin: 0,
          }}
        >
          {compound.oneLineDescription}
        </p>
      )}

      {/* Investigational callout + WADA chip — both shown on the card
          per regulatory / athletic-eligibility guidance. WADA chip is
          intentionally small and inline so it doesn't dominate the
          tile; full disclosure can live in the detail panel. */}
      {(compound.isInvestigational || isWadaProhibited(compound)) && (
        <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 6 }}>
          {compound.isInvestigational && (
            <InvestigationalCallout
              note={compound.investigationalNote}
              size="sm"
              testId={`compound-card-investigational-${compound.slug}`}
            />
          )}
          {isWadaProhibited(compound) && (
            <span
              data-testid={`compound-card-wada-${compound.slug}`}
              role="note"
              style={{
                alignSelf: "flex-start",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.18em",
                padding: "3px 8px",
                background: isExpanded ? "rgba(250,246,240,0.12)" : C.bone,
                color: isExpanded ? C.peach : C.warmGray,
                border: `1px solid ${isExpanded ? "rgba(250,246,240,0.18)" : C.warmGray3}`,
                textTransform: "uppercase",
              }}
            >
              ⚑ WADA-PROHIBITED
            </span>
          )}
        </div>
      )}

      <div
        style={{
          marginTop: "auto",
          paddingTop: 14,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 10,
        }}
      >
        {startingFrom != null && (
          <span style={{ fontSize: 13, color: isExpanded ? C.peach : C.ink, letterSpacing: "0.04em" }}>
            <span style={{ fontWeight: 500, opacity: 0.7 }}>FROM </span>
            <strong style={{ fontWeight: 800 }}>{fmtMoney(startingFrom)}</strong>
            <span style={{ fontWeight: 500, opacity: 0.7 }}>/mo</span>
          </span>
        )}
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.22em",
            color: isExpanded ? C.peach : C.ink,
          }}
        >
          {isExpanded ? "VIEWING ↓" : (isActiveForPurchase ? "MORE INFO →" : "EXPLORE →")}
        </span>
      </div>
    </button>
  );
}

/* ---------- detail panel with stage slider ---------- */

const CompoundDetailPanel = forwardRef(function CompoundDetailPanel(
  {
    compounds,
    expandedSlug,
    onSwitch,
    onClose,
    programColor,
    programAccent,
    programName,
    isActiveForPurchase,
    onAddCompound,
    onRemoveCompound,
    inPlanCompoundSlug,
    inPlanStageKey,
  },
  ref,
) {
  const compound = compounds.find((c) => c.slug === expandedSlug) || null;

  // Stage slider state — initialized to null and resolved against the
  // current compound's first stage. stageKey is now an opaque
  // admin-authored string (no longer constrained to "start" / "build" /
  // "maintain"), so we never hard-code a default value here. Phase B
  // doc explicitly: "Always render stage.stageLabel verbatim and pass
  // stage.stageKey opaquely back as selectedStage on submission."
  const [activeStageKey, setActiveStageKey] = useState(null);
  // Reset stage selection when the member switches compounds so they
  // always land on the new compound's first stage rather than carrying
  // over a (potentially missing) stage key from the previous compound.
  useEffect(() => {
    const next = compounds.find((c) => c.slug === expandedSlug);
    const firstKey = next?.stages?.[0]?.stageKey ?? null;
    setActiveStageKey(firstKey);
  }, [expandedSlug, compounds]);

  if (!compound) return <div ref={ref} aria-hidden style={{ height: 0 }} />;

  const stages = (compound.stages || []).filter((s) => s && s.stageKey);
  // BE Phase B convenience flag. When true we suppress the stage label
  // entirely — regardless of what stageLabel says — and present the
  // compound as a single offering. Be defensive in case an environment
  // hasn't redeployed yet by also collapsing the UI when there's only
  // one stage in the array.
  const isSingleStage = compound.singleStage === true || stages.length <= 1;
  const fallbackKey = stages[0]?.stageKey || null;
  const activeIdx = Math.max(0, stages.findIndex((s) => s.stageKey === (activeStageKey ?? fallbackKey)));
  const activeStage = stages[activeIdx] || stages[0] || null;
  const isStageBuyable = !!activeStage && !!activeStage.productId;
  const isInPlan = inPlanCompoundSlug === compound.slug && inPlanStageKey === activeStage?.stageKey;

  const onAdd = () => {
    if (!activeStage) return;
    onAddCompound?.(compound, activeStage);
  };

  return (
    <div
      ref={ref}
      data-testid="compound-detail-panel"
      style={{
        marginTop: 14,
        background: C.bone,
        padding: 28,
        borderTop: `4px solid ${programAccent}`,
      }}
    >
      {/* Compound switcher pills + close — pill aesthetic matches the
          legacy tier picker. Pills are rounded, hairline-bordered when
          inactive, solid black fill when active. The close affordance
          is a small circular × in the top-right corner. Suppressed
          entirely on single-compound programs since there's nothing to
          switch between (and closing leaves nothing to view). */}
      {compounds.length > 1 && (
        <div style={{ position: "relative", paddingRight: 36 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {compounds.map((c) => {
              const active = c.slug === compound.slug;
              return (
                <button
                  key={c.slug}
                  type="button"
                  data-testid={`compound-switch-${c.slug}`}
                  onClick={() => onSwitch(c.slug)}
                  style={{
                    padding: "10px 22px",
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: "0.22em",
                    background: active ? C.ink : "transparent",
                    color: active ? C.cream : C.ink,
                    border: `1.5px solid ${C.ink}`,
                    borderRadius: 9999,
                    cursor: "pointer",
                    textTransform: "uppercase",
                    transition: "background 160ms ease, color 160ms ease",
                  }}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            data-testid="compound-detail-close"
            onClick={onClose}
            aria-label="Close compound detail"
            style={{
              position: "absolute",
              top: -2,
              right: 0,
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "transparent",
              border: `1px solid ${C.warmGray3}`,
              color: C.warmGray,
              fontSize: 14,
              lineHeight: "26px",
              textAlign: "center",
              cursor: "pointer",
              padding: 0,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Compound headline */}
      <div style={{ marginTop: 22 }}>
        {compound.headline && (
          <Headline before={compound.name.toUpperCase()} italic="·" size={38} className="" />
        )}
        {compound.headline && (
          <p
            style={{
              marginTop: 10,
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontStyle: "italic",
              fontSize: 22,
              color: C.ink,
              lineHeight: 1.4,
              maxWidth: 720,
            }}
          >
            {compound.headline}
          </p>
        )}
      </div>

      {/* Stage slider — suppressed for single-stage compounds. We show
          a SingleStageDetail block instead so the page reads as "the
          offering" rather than a degenerate single-pill row. */}
      {stages.length > 0 && !isSingleStage && (
        <StageSlider
          stages={stages}
          activeIdx={activeIdx}
          onChange={(idx) => setActiveStageKey(stages[idx]?.stageKey ?? fallbackKey)}
          programAccent={programAccent}
          programColor={programColor}
        />
      )}
      {stages.length > 0 && isSingleStage && (
        <SingleStageDetail
          stage={activeStage}
          programColor={programColor}
          programAccent={programAccent}
        />
      )}

      {/* Add to plan / remove from plan / coming soon. The wrapper is
          STICKY scoped to the detail panel — once the member scrolls
          past the natural button position to read the rich content
          below (HOW IT WORKS, WHO IT SUITS, …), the Add CTA pins
          beneath the app header so they can act on it the moment they
          decide. Sticky scope is the CompoundDetailPanel itself, so
          the button releases when the entire detail panel scrolls out
          of view. */}
      <div
        style={{
          marginTop: 24,
          position: "sticky",
          // App header is sticky at top: 0 with ~56px height — pin the
          // CTA right beneath it so the two read as a single fixed
          // band when stuck.
          top: 56,
          zIndex: 20,
          // Bone background extends the bottom border of the
          // bone-coloured detail panel underneath the button so
          // scrolling content doesn't bleed through. Negative-x
          // margins bleed to the panel's edge for a clean full-width
          // band when stuck.
          background: C.bone,
          marginLeft: -28,
          marginRight: -28,
          padding: "12px 28px",
          // Subtle hairline below so members see the band as a
          // distinct stuck affordance rather than mid-page float.
          boxShadow: "0 6px 14px rgba(15,14,13,0.04)",
        }}
      >
        {!isActiveForPurchase ? (
          <p style={{ fontSize: 13, color: C.warmGray, fontStyle: "italic", lineHeight: 1.55 }}>
            {programName} isn't open for new members yet. Browse the research below and add yourself to the waitlist when it goes live.
          </p>
        ) : !isStageBuyable ? (
          <Button variant="ghost" block disabled testId="compound-stage-coming-soon">
            COMING SOON — {isSingleStage ? compound.name.toUpperCase() : (activeStage?.stageLabel?.toUpperCase() || "STAGE")} NOT YET AVAILABLE
          </Button>
        ) : isInPlan ? (
          <Button
            variant="ghost"
            block
            testId="compound-remove-from-plan"
            onClick={() => onRemoveCompound?.()}
          >
            ✓ ADDED · TAP TO REMOVE
          </Button>
        ) : (
          <Button
            variant="primary"
            block
            testId="compound-add-to-plan"
            onClick={onAdd}
          >
            {isSingleStage
              ? `ADD ${compound.name.toUpperCase()} · ${fmtMoney(activeStage.monthlyPrice)}/MO →`
              : `ADD ${compound.name.toUpperCase()} · ${activeStage.stageLabel.toUpperCase()} · ${fmtMoney(activeStage.monthlyPrice)}/MO →`}
          </Button>
        )}
      </div>

      {/* Compound rich content — collapsible sections */}
      <CompoundContent compound={compound} />
    </div>
  );
});

/* ---------- single-stage detail (singleStage compounds) ---------- */
// Used when a compound has only one stage — we don't need the
// Start/Build/Maintain progression UI; the stage content IS the offering.
// Same per-stage info as StageSlider's detail panel but no pills, no
// arrows, no stage label header.
function SingleStageDetail({ stage, programColor, programAccent }) {
  if (!stage) return null;
  const weekly = stage.costFraming || (Number(stage.monthlyPrice) > 0 ? `$${Math.round(Number(stage.monthlyPrice) / 4)}/week` : "");
  return (
    <section
      data-testid={`single-stage-detail-${stage.stageKey}`}
      style={{
        marginTop: 28,
        background: programColor,
        color: C.ink,
        padding: 22,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: programAccent }}>
            ★ THE OFFERING
          </div>
          {stage.product?.name && (
            <div style={{ marginTop: 6, fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em", textTransform: "uppercase", color: C.ink }}>
              {stage.product.name}
            </div>
          )}
        </div>
        {Number(stage.monthlyPrice) > 0 && (
          <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
            <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.02em", color: C.ink }}>
              {fmtMoney(stage.monthlyPrice)}
            </span>
            <span style={{ fontSize: 12, color: programAccent, fontWeight: 600 }}>/mo</span>
            {weekly && (
              <div style={{ fontSize: 11, color: programAccent, marginTop: 2, letterSpacing: "0.04em" }}>
                {weekly}
              </div>
            )}
          </div>
        )}
      </div>

      {(stage.supplyDurationLabel || stage.product?.dosage) && (
        <div style={{ fontSize: 13, color: programAccent, letterSpacing: "0.04em" }}>
          {stage.product?.dosage && <span>{stage.product.dosage} vial</span>}
          {stage.product?.dosage && stage.supplyDurationLabel && <span> · </span>}
          {stage.supplyDurationLabel}
        </div>
      )}

      {stage.stageDescription && (
        <p style={{ fontSize: 14, lineHeight: 1.6, color: C.ink, margin: 0 }}>
          {stage.stageDescription}
        </p>
      )}

      <StageDetailRow label="WHO IS THIS FOR" body={stage.whoIsThisFor} programAccent={programAccent} />
      <StageDetailRow label="WHAT YOU RECEIVE" body={stage.whatYouReceive} programAccent={programAccent} />
      <StageDetailRow label="WHAT YOUR PHYSICIAN DOES" body={stage.whatPhysicianDoes} programAccent={programAccent} />
      <StageDetailRow label="WHAT TO EXPECT" body={stage.whatToExpect} programAccent={programAccent} />
      {/* WHAT'S INCLUDED — prefer the structured `bundleItems[]` array
          when present so members see "1 × Semaglutide 20mg vial · 1 ×
          Bacteriostatic water" rendered cleanly. Falls back to the
          admin-authored prose `accompanyingSupplies` string if no
          structured bundle exists. */}
      <BundleList stage={stage} programAccent={programAccent} />
    </section>
  );
}

/* ---------- stage slider ---------- */
function StageSlider({ stages, activeIdx, onChange, programAccent, programColor }) {
  const stage = stages[activeIdx] || stages[0];
  if (!stage) return null;
  const weekly = stage.costFraming || (Number(stage.monthlyPrice) > 0 ? `$${Math.round(Number(stage.monthlyPrice) / 4)}/week` : "");

  return (
    <section data-testid="stage-slider" style={{ marginTop: 28 }}>
      {/* Stage pills — pill aesthetic matches the compound switcher
          (and the legacy tier picker). No arrow buttons / wrapper box;
          pills are tappable directly. The active pill takes the program
          accent color so members see "their stage" at a glance. */}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          justifyContent: "flex-start",
        }}
      >
        {stages.map((s, i) => {
          const active = i === activeIdx;
          return (
            <button
              key={s.stageKey}
              type="button"
              data-testid={`stage-pill-${s.stageKey}`}
              onClick={() => onChange(i)}
              style={{
                padding: "10px 22px",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.22em",
                background: active ? programAccent : "transparent",
                color: active ? C.cream : C.ink,
                border: `1.5px solid ${active ? programAccent : C.ink}`,
                borderRadius: 9999,
                cursor: "pointer",
                textTransform: "uppercase",
                transition: "background 160ms ease, color 160ms ease, border-color 160ms ease",
              }}
            >
              {s.stageLabel || s.stageKey}
            </button>
          );
        })}
      </div>

      {/* Stage detail panel */}
      <div
        data-testid={`stage-detail-${stage.stageKey}`}
        style={{
          marginTop: 12,
          background: programColor,
          color: C.ink,
          padding: 22,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: programAccent }}>
              ★ {stage.stageLabel?.toUpperCase() || stage.stageKey?.toUpperCase()}
            </div>
            {stage.product?.name && (
              <div style={{ marginTop: 6, fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em", textTransform: "uppercase", color: C.ink }}>
                {stage.product.name}
              </div>
            )}
          </div>
          {Number(stage.monthlyPrice) > 0 && (
            <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
              <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.02em", color: C.ink }}>
                {fmtMoney(stage.monthlyPrice)}
              </span>
              <span style={{ fontSize: 12, color: programAccent, fontWeight: 600 }}>/mo</span>
              {weekly && (
                <div style={{ fontSize: 11, color: programAccent, marginTop: 2, letterSpacing: "0.04em" }}>
                  {weekly}
                </div>
              )}
            </div>
          )}
        </div>

        {(stage.supplyDurationLabel || stage.product?.dosage) && (
          <div style={{ fontSize: 13, color: programAccent, letterSpacing: "0.04em" }}>
            {stage.product?.dosage && <span>{stage.product.dosage} vial</span>}
            {stage.product?.dosage && stage.supplyDurationLabel && <span> · </span>}
            {stage.supplyDurationLabel}
          </div>
        )}

        {stage.stageDescription && (
          <p style={{ fontSize: 14, lineHeight: 1.6, color: C.ink, margin: 0 }}>
            {stage.stageDescription}
          </p>
        )}

        {/* Quad of stage details — only render rows the BE populated */}
        <StageDetailRow label="WHO IS THIS FOR" body={stage.whoIsThisFor} programAccent={programAccent} />
        <StageDetailRow label="WHAT YOU RECEIVE" body={stage.whatYouReceive} programAccent={programAccent} />
        <StageDetailRow label="WHAT YOUR PHYSICIAN DOES" body={stage.whatPhysicianDoes} programAccent={programAccent} />
        <StageDetailRow label="WHAT TO EXPECT" body={stage.whatToExpect} programAccent={programAccent} />
        {/* WHAT'S INCLUDED — see SingleStageDetail for rationale. */}
        <BundleList stage={stage} programAccent={programAccent} />
      </div>
    </section>
  );
}

function StageDetailRow({ label, body, programAccent }) {
  if (!body) return null;
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: programAccent }}>
        {label}
      </div>
      <p style={{ marginTop: 4, fontSize: 13, lineHeight: 1.55, color: C.ink, margin: 0 }}>
        {body}
      </p>
    </div>
  );
}

// WHAT'S INCLUDED renderer — prefers the structured `bundleItems[]`
// shipped by the BE Phase B+ contract when available so the member sees
// each line item with quantity. Falls back to the admin-authored
// `accompanyingSupplies` prose string otherwise. Renders nothing when
// neither source has content.
function BundleList({ stage, programAccent }) {
  const items = Array.isArray(stage?.bundleItems) ? stage.bundleItems : [];
  if (items.length > 0) {
    return (
      <div data-testid="stage-bundle-list">
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: programAccent }}>
          WHAT'S INCLUDED
        </div>
        <ul style={{ margin: "6px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
          {items.map((item, i) => {
            const qty = Number(item?.quantity) > 0 ? Number(item.quantity) : 1;
            const name = item?.product?.name || item?.name || "";
            const dosage = item?.product?.dosage;
            // Avoid duplicating the dosage when it's already part of
            // the product name (e.g., "Semaglutide 20mg" + dosage "20mg"
            // → keep the name as-is).
            const dosageSuffix = dosage && !name.toLowerCase().includes(String(dosage).toLowerCase())
              ? ` · ${dosage}`
              : "";
            if (!name) return null;
            return (
              <li
                key={item?.productId || i}
                data-testid={`stage-bundle-item-${i}`}
                style={{ fontSize: 13, color: C.ink, lineHeight: 1.55, display: "flex", gap: 8 }}
              >
                <span style={{ color: programAccent, fontWeight: 700, minWidth: 24 }}>
                  {qty}×
                </span>
                <span>
                  {name}{dosageSuffix}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
  if (stage?.accompanyingSupplies) {
    return <StageDetailRow label="WHAT'S INCLUDED" body={stage.accompanyingSupplies} programAccent={programAccent} />;
  }
  return null;
}

/* ---------- compound rich content (collapsibles) ---------- */
function CompoundContent({ compound }) {
  const sections = [
    { key: "howItWorks",       label: "HOW IT WORKS",        body: compound.howItWorks },
    { key: "whoItSuits",       label: "WHO IT SUITS",        body: compound.whoItSuits },
    { key: "howYoullTakeIt",   label: "HOW YOU'LL TAKE IT",  body: compound.howYoullTakeIt },
    { key: "beyondWeight",     label: "BEYOND THE OBVIOUS",  body: compound.beyondWeight },
    { key: "sideEffects",      label: "SIDE EFFECTS",        body: compound.sideEffects },
    { key: "contraindications",label: "CONTRAINDICATIONS",   body: compound.contraindications },
    { key: "pregnancyWarning", label: "PREGNANCY WARNING",   body: compound.pregnancyWarning },
  ].filter((s) => s.body && String(s.body).trim().length > 0);

  const research = Array.isArray(compound.whatResearchShows) ? compound.whatResearchShows : [];

  if (sections.length === 0 && research.length === 0) return null;

  return (
    <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 4 }}>
      {sections.map((s) => (
        <Disclosure key={s.key} label={s.label}>
          <p style={{ fontSize: 14, lineHeight: 1.65, color: C.ink, margin: 0, whiteSpace: "pre-wrap" }}>
            {s.body}
          </p>
        </Disclosure>
      ))}
      {research.length > 0 && (
        <Disclosure label="WHAT THE RESEARCH SHOWS" defaultOpen={false}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {research.map((r, i) => (
              <div
                key={i}
                data-testid={`research-citation-${i}`}
                style={{
                  padding: "12px 14px",
                  background: C.pure,
                  borderLeft: `3px solid ${C.peachDeep}`,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: C.peachDeep }}>
                  {r.trialName} {r.year ? `· ${r.year}` : ""}
                </div>
                {r.participants && (
                  <div style={{ marginTop: 4, fontSize: 13, color: C.warmGray }}>
                    {r.participants}
                  </div>
                )}
                {r.result && (
                  <p style={{ marginTop: 8, fontSize: 14, color: C.ink, lineHeight: 1.55, margin: 0 }}>
                    {r.result}
                  </p>
                )}
                {r.source && (
                  <div style={{ marginTop: 8, fontSize: 11, color: C.warmGray, fontStyle: "italic" }}>
                    {r.source}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Disclosure>
      )}
    </div>
  );
}

function Disclosure({ label, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      data-testid={`compound-disclosure-${label.toLowerCase().replace(/\s+/g, "-")}`}
      style={{ borderTop: `1px solid ${C.warmGray3}` }}
    >
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        style={{
          width: "100%",
          background: "transparent",
          border: 0,
          padding: "16px 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.ink }}>
          {label}
        </span>
        <span aria-hidden style={{ fontSize: 14, color: C.warmGray, transition: "transform 160ms ease", transform: open ? "rotate(180deg)" : "none" }}>
          ▾
        </span>
      </button>
      {open && <div style={{ paddingBottom: 18 }}>{children}</div>}
    </div>
  );
}

/* ---------- decision helper (comparison table + self-awareness questions) ---------- */
function DecisionHelper({ helper, compounds, programAccent, programSlug, isSingleCompound }) {
  // BE March 2026 contract — comparison table renders only when the
  // admin explicitly enables it. Without the flag we skip the grid even
  // if rows exist (admin may be drafting). Self-awareness questions are
  // independently fetched/rendered by their own component and stay
  // visible even when the matrix is disabled.
  // Single-compound programs never render the comparison table at all
  // (nothing to compare) — but they keep the self-awareness section
  // since the questions remain valuable even with one compound.
  const tableEnabled = !isSingleCompound && helper?.comparisonTable?.enabled === true;
  const rows = tableEnabled && Array.isArray(helper?.comparisonTable?.rows)
    ? helper.comparisonTable.rows
    : [];

  return (
    <section data-testid="decision-helper" style={{ marginTop: 40 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: programAccent }}>
        {isSingleCompound ? "★ BEFORE YOU BEGIN" : "★ NOT SURE WHICH PATH?"}
      </div>
      <Headline
        before={isSingleCompound ? "A FEW HONEST" : "LET'S"}
        italic={isSingleCompound ? "questions." : "walk you through it."}
        size={36}
        className="mt-2"
      />

      {rows.length > 0 && (
        <ComparisonTable rows={rows} compounds={compounds} programAccent={programAccent} />
      )}

      {programSlug && (
        <SelfAwarenessQuestions programSlug={programSlug} programAccent={programAccent} />
      )}
    </section>
  );
}

// Render the comparison table. Admins author rows in the canonical
// flat shape per the March 2026 BE handoff:
//   { label, <compoundSlug>: value, <compoundSlug>: value, ... }
// We also accept two legacy shapes for safety during migration:
//   { label, values: { [compoundSlug]: value } }
//   { label, cells:  [value, value, value] }   indexed by compound order
// Falls back to em-dash if the row is missing a value.
function ComparisonTable({ rows, compounds, programAccent }) {
  return (
    <div
      data-testid="decision-helper-table"
      style={{
        marginTop: 22,
        background: C.pure,
        border: `1.5px solid ${C.warmGray3}`,
        overflowX: "auto",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 13,
          color: C.ink,
          minWidth: compounds.length > 2 ? 540 : "auto",
        }}
      >
        <thead>
          <tr>
            <th
              scope="col"
              style={{
                textAlign: "left",
                padding: "14px 16px",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.22em",
                color: C.warmGray,
                background: C.bone,
                borderBottom: `1px solid ${C.warmGray3}`,
              }}
            />
            {compounds.map((c) => (
              <th
                key={c.slug}
                scope="col"
                style={{
                  textAlign: "left",
                  padding: "14px 16px",
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.18em",
                  color: programAccent,
                  background: C.bone,
                  textTransform: "uppercase",
                  borderBottom: `1px solid ${C.warmGray3}`,
                }}
              >
                {c.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={row.id || ri}>
              <th
                scope="row"
                style={{
                  textAlign: "left",
                  padding: "14px 16px",
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.ink,
                  letterSpacing: "0.04em",
                  borderBottom: `1px solid ${C.warmGray4}`,
                  verticalAlign: "top",
                  width: "30%",
                }}
              >
                {row.label || row.title || ""}
              </th>
              {compounds.map((c, ci) => {
                // Canonical flat shape — value lives at `row[<slug>]`.
                // Then legacy fallbacks for in-flight migrations.
                const v = row?.[c.slug] ?? row.values?.[c.slug] ?? row.cells?.[ci] ?? "";
                return (
                  <td
                    key={c.slug}
                    style={{
                      padding: "14px 16px",
                      fontSize: 13,
                      color: v ? C.ink : C.warmGray2,
                      lineHeight: 1.5,
                      borderBottom: `1px solid ${C.warmGray4}`,
                      verticalAlign: "top",
                    }}
                  >
                    {v || "—"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}




